import { v4 as uuidv4 } from 'uuid';
import React, { memo, useCallback } from 'react';

import { Box, Chip, Stack, Button, Divider, Tooltip } from '@mui/material';

import useLocales from '../../../hooks/useLocales';
import Scrollbar from '../../../components/Scrollbar';
import useNotifications from '../../../hooks/useNotifications';
import { Collaborator, ElementProps } from '../../../@types/forms/types';
import { generateColor } from '../../../@types/forms/utils/generateColor';

// ----------------------------------------------------------------------

type CollaboratorsEditFormProps = {
  collaborators: Collaborator[];
  setCollaborators: (collaborator: Collaborator[]) => void;
  elements: ElementProps[];
};

const CollaboratorsEditForm = ({
  collaborators,
  setCollaborators,
  elements,
}: CollaboratorsEditFormProps) => {
  const { t } = useLocales();
  const { enqueueSuccess, enqueueError } = useNotifications();

  const handleAddCollaborator = () => {
    const newCollaborator: Collaborator = {
      collaboratorId: uuidv4(),
      role: 'collaborator',
      email: undefined,
      fields: [],
      color: generateColor(collaborators),
      canFill: true,
      order: collaborators.length,
    };

    setCollaborators([...collaborators, newCollaborator]);
  };

  const handleDelete = (index: number) => {
    if (collaborators[index].fields.length > 0) {
      enqueueError(t('forms.collaboratorHasAssignedFields'));
      return;
    }

    let collaboratorCount = 1;
    const newCollaborators = collaborators
      .filter((_, i) => i !== index)
      .map((c) => {
        if (c.role === 'collaborator') {
          const updatedCollaborator = {
            ...c,
            order: collaboratorCount,
          };
          collaboratorCount += 1;
          return updatedCollaborator;
        }
        return c;
      });

    setCollaborators(newCollaborators);
    enqueueSuccess(t('forms.collaboratorDeleted'));
  };

  const getFieldsTooltip = useCallback(
    (collaboratorId: string) => {
      const fields = elements
        .filter((e) => e.collaboratorId === collaboratorId)
        .map((f) => `${f.label} (${t('forms.page')}: ${f.page})`);

      return fields.length > 0 ? fields.join('\n') : t('forms.noFieldsAssigned');
    },
    [elements, t]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Scrollbar sx={{ maxHeight: '112px' }}>
        {collaborators.length > 0 && (
          <Stack direction="column" spacing={1} sx={{
            alignItems: "flex-start"
          }}>
            {collaborators.map((c, index) => (
              <Tooltip
                key={c.collaboratorId}
                title={getFieldsTooltip(c.collaboratorId)}
                arrow
                slotProps={{
                  tooltip: {
                    sx: { whiteSpace: 'pre-line' },
                  },
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -14],
                        },
                      },
                    ],
                  },
                }}
              >
                <Chip
                  label={
                    c.order === 0 ? t('forms.owner') : t('forms.collaborator', { number: c.order })
                  }
                  sx={{ backgroundColor: c.color }}
                  onDelete={c.role === 'collaborator' ? () => handleDelete(index) : undefined}
                />
              </Tooltip>
            ))}
          </Stack>
        )}
      </Scrollbar>
      <Divider />
      <Button
        variant="contained"
        color="secondary"
        fullWidth
        onClick={handleAddCollaborator}
        id="p62d6sbs"
      >
        {t('forms.addNewCollaborator')}
      </Button>
    </Box>
  );
};

export default memo(CollaboratorsEditForm);
